import { forwardRef, InputHTMLAttributes, LegacyRef, useState, ChangeEvent } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

type Option<T> = {
  value: T;
  label: string;
};

type SelectInputProps<T> = { onChange?: (value: T) => void; options: Option<T>[] } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    labelClassName?: string;
    required?: boolean;
  } & { value: T; searchable?: boolean; searchablePlaceholder?: string };

const SelectInput = forwardRef(
  <T,>(
    {
      label,
      error,
      wrapperClassName,
      labelClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      options,
      className,
      searchable = false,
      searchablePlaceholder = 'Search...',
      required,
      ...args
    }: SelectInputProps<T>,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const [searchQuery, setSearchQuery] = useState('');

    const handleSelect = (value: T) => {
      if (onChange) {
        onChange(value);
      }

      setSearchQuery('');

      // @ts-expect-error - blur() is a valid method
      document?.activeElement?.blur();
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    };

    const availableOptions = !searchable
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">
              {label}
              {required && <span className="text-error"> *</span>}
            </span>
          </label>
        ) : null}
        <label
          className={cn(
            'input flex items-center justify-between gap-2 dropdown cursor-pointer max-w-full group',
            labelClassName,
            {
              'input-ghost': color === 'ghost',
              'input-primary': color === 'primary',
              'input-secondary': color === 'secondary',
              'input-accent': color === 'accent',
              'input-info': color === 'info',
              'input-success': color === 'success',
              'input-warning': color === 'warning',
              'input-error': error || color === 'error',
              'input-xs': size === 'xs',
              'input-sm': size === 'sm',
              'input-md': size === 'md' || size,
              'input-lg': size === 'lg',
              'input-bordered': bordered,
            }
          )}
        >
          <input
            tabIndex={0}
            className={cn('text-base grow cursor-pointer overflow-auto max-w-[calc(100%-1rem)]', className)}
            ref={ref}
            readOnly
            value={options.find((option) => option.value === value)?.label ?? ''}
            {...args}
          />
          <ul
            tabIndex={0}
            className={cn(
              'dropdown-content menu gap-1 bg-white rounded-xl z-10 border border-gray-200 shadow-lg w-full absolute left-0 top-[calc(100%+6px)] max-h-[220px] overflow-y-auto flex-nowrap hidden group-focus-within:flex',
              {
                'p-1.5': !searchable,
                'px-1.5 pt-0 pb-1.5': searchable,
              }
            )}
          >
            {searchable ? (
              <li className="sticky top-0 py-2 z-10 bg-white">
                <input
                  placeholder={searchablePlaceholder}
                  className="bg-transparent border input-bordered rounded-lg cursor-text"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </li>
            ) : null}
            {availableOptions.map((option) => (
              <li key={`${option.label}_${args.name ?? ''}_${option.value}`}>
                <button
                  type="button"
                  className={cn(
                    'w-full rounded-lg px-3 py-2.5 text-sm font-medium text-left transition-colors hover:bg-gray-100 focus:bg-gray-100',
                    {
                      '!bg-black !text-white hover:!bg-black': option.value === value,
                    }
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
          <ChevronDownIcon className="w-4 shrink-0" />
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
) as <T>(props: SelectInputProps<T> & { ref?: LegacyRef<HTMLInputElement> }) => JSX.Element;

export default SelectInput;
